import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiCrossword } from '../utils/api';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import PageSEO from '../components/PageSEO';

const MIN_LEN = 2;
const MAX_LEN = 15;
const DEFAULT_LEN = 5;

// Turn the per-box letters into the pattern the API expects: empty box → "_".
const toPattern = letters => letters.map(c => c || '_').join('');

// Parse a raw string ("c_t", "H?USE", "light") into a fixed-length letter array.
function parseToLetters(str) {
  const chars = str.toLowerCase().replace(/[^a-z_?]/g, '').split('');
  return chars.map(c => (c === '_' || c === '?' ? '' : c));
}

export default function CrosswordSolver() {
  const [length, setLength]   = useState(DEFAULT_LEN);
  const [letters, setLetters] = useState(Array(DEFAULT_LEN).fill(''));
  const [clue, setClue]       = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const boxRefs = useRef([]);

  const focusBox = i => {
    const el = boxRefs.current[i];
    if (el) { el.focus(); el.select(); }
  };

  // Resize the letter boxes when the chosen length changes, keeping typed letters.
  const changeLength = useCallback(next => {
    const n = Math.max(MIN_LEN, Math.min(MAX_LEN, next));
    setLength(n);
    setLetters(prev => {
      const arr = Array(n).fill('');
      for (let i = 0; i < n; i++) arr[i] = prev[i] || '';
      return arr;
    });
    setResults(null);
    setTimeout(() => focusBox(0), 0);
  }, []);

  function setBox(i, val) {
    const c = val.toLowerCase().replace(/[^a-z]/g, '').slice(-1); // last typed letter only
    setLetters(prev => {
      const arr = [...prev];
      arr[i] = c;
      return arr;
    });
    if (c && i < length - 1) focusBox(i + 1);
  }

  function onKeyDown(i, e) {
    if (e.key === 'Backspace' && !letters[i] && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
      setLetters(prev => { const arr = [...prev]; arr[i - 1] = ''; return arr; });
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault(); focusBox(i - 1);
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault(); focusBox(i + 1);
    } else if (e.key === 'Enter') {
      handleSolve(e);
    }
  }

  // Paste "c_t" / "house" anywhere and it fills the whole row (and resizes if needed).
  function onPaste(i, e) {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text) return;
    e.preventDefault();
    const parsed = parseToLetters(text);
    if (!parsed.length) return;
    const n = Math.max(MIN_LEN, Math.min(MAX_LEN, Math.max(length, i + parsed.length)));
    setLength(n);
    setLetters(prev => {
      const arr = Array(n).fill('').map((_, k) => prev[k] || '');
      for (let k = 0; k < parsed.length && i + k < n; k++) arr[i + k] = parsed[k];
      return arr;
    });
  }

  function applyExample(str) {
    const parsed = parseToLetters(str);
    changeLength(parsed.length);
    setLetters(parsed);
    setResults(null);
    setTimeout(() => focusBox(0), 0);
  }

  function clearAll() {
    setLetters(Array(length).fill(''));
    setResults(null);
    focusBox(0);
  }

  async function handleSolve(e) {
    e.preventDefault();
    const pattern = toPattern(letters);
    const clueText = clue.trim();
    if (!pattern && !clueText) return;
    setLoading(true); setError(''); setResults(null);
    try {
      const { data } = await apiCrossword(pattern, clueText);
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setLoading(false); }
  }

  const EXAMPLES = ['c_t', '_ight', 'h__se', 'd_g', '_ream', 'b_rd'];
  const pattern = toPattern(letters);
  const filledCount = letters.filter(Boolean).length;

  return (
    <div className="game-page">
      <PageSEO page="crossword" />
      <div className="container" style={{maxWidth:700}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">✏️</span>
          <h1>Crossword Solver</h1>
          <p>Solve any crossword clue instantly — pick the number of letters, fill in the ones you already have from crossing answers, and get every word that fits.</p>
        </div>

        <div className="solver-card">
          <form onSubmit={handleSolve}>
            {/* Word length selector */}
            <div className="mb-3">
              <label className="fw-bold mb-2 d-block">Number of letters</label>
              <div className="d-flex flex-wrap gap-1">
                {Array.from({length: MAX_LEN - MIN_LEN + 1}, (_, k) => k + MIN_LEN).map(n => (
                  <button
                    key={n} type="button"
                    onClick={() => changeLength(n)}
                    className="fw-bold"
                    style={{
                      width:34, height:34, borderRadius:8, cursor:'pointer',
                      fontSize:'0.9rem', fontFamily:'Nunito,sans-serif',
                      border: n === length ? '2px solid var(--primary)' : '1.5px solid #e5e7f2',
                      background: n === length ? 'var(--primary)' : '#fff',
                      color: n === length ? '#fff' : '#64748b',
                    }}
                  >{n}</button>
                ))}
              </div>
            </div>

            {/* Per-letter boxes */}
            <div className="mb-2">
              <label className="fw-bold mb-2 d-block">
                Letters you already have
                <span className="text-muted fw-normal ms-2" style={{fontSize:'0.85rem'}}>
                  (leave unknown squares empty)
                </span>
              </label>
              <div className="d-flex flex-wrap gap-2">
                {letters.map((c, i) => (
                  <input
                    key={i}
                    ref={el => { boxRefs.current[i] = el; }}
                    value={c.toUpperCase()}
                    onChange={e => setBox(i, e.target.value)}
                    onKeyDown={e => onKeyDown(i, e)}
                    onPaste={e => onPaste(i, e)}
                    onFocus={e => e.target.select()}
                    inputMode="text"
                    maxLength={1}
                    aria-label={`Letter ${i + 1}`}
                    autoFocus={i === 0}
                    style={{
                      width:46, height:54, textAlign:'center', textTransform:'uppercase',
                      fontSize:'1.5rem', fontWeight:800, fontFamily:'Nunito,sans-serif',
                      color:'#1e293b', borderRadius:10, outline:'none',
                      border: c ? '2px solid var(--primary)' : '2px solid #e5e7f2',
                      background: c ? '#f5f3ff' : '#fff', caretColor:'var(--primary)',
                    }}
                  />
                ))}
              </div>
              <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
                <p className="pattern-hint mb-0">
                  Pattern: <code style={{fontSize:'0.95rem'}}>{pattern}</code>
                </p>
                <button type="button" onClick={clearAll} disabled={filledCount === 0}
                  className="btn btn-sm fw-bold"
                  style={{borderRadius:8, border:'1.5px solid #e5e7f2', background:'#fff',
                          color: filledCount === 0 ? '#cbd5e1' : '#64748b',
                          padding:'0.15rem 0.7rem', fontSize:'0.8rem',
                          cursor: filledCount === 0 ? 'not-allowed' : 'pointer'}}>
                  ✕ Clear letters
                </button>
              </div>
            </div>

            {/* Clue (optional) — solve by meaning */}
            <div className="mb-3 mt-3">
              <label className="fw-bold mb-1 d-block">
                Clue <span className="text-muted fw-normal" style={{fontSize:'0.85rem'}}>(optional)</span>
              </label>
              <input
                className="big-input no-upper"
                value={clue}
                onChange={e => setClue(e.target.value)}
                placeholder="e.g. sea eagle, opera solo, poker stake"
                maxLength={80}
              />
              <p className="pattern-hint mb-0">
                Know the clue's meaning? Type it here and we'll match answers by definition —
                the number of letters and known letters above narrow it down. Works best for
                dictionary-style clues (not names or places).
              </p>
            </div>

            {/* Quick examples */}
            <div className="mb-3">
              <small className="text-muted fw-bold me-2">Quick examples:</small>
              {EXAMPLES.map(ex => (
                <button
                  key={ex} type="button"
                  className="btn btn-sm me-1 mb-1 fw-bold"
                  style={{borderRadius:'50px', background:'var(--border)', border:'none',
                          fontFamily:'monospace', fontSize:'0.85rem', padding:'0.2rem 0.6rem'}}
                  onClick={() => applyExample(ex)}
                >{ex}</button>
              ))}
            </div>

            <button type="submit" className="solve-btn" disabled={loading || (!pattern && !clue.trim())}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Searching…</>
                : (clue.trim() ? '🔎 Find Answers' : '✏️ Find Words')}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results !== null && (
          <div className="solver-card">
            {results.mode === 'clue' ? (
              <>
                <p className="result-count">
                  Found <strong>{results.count}</strong> possible answer{results.count !== 1 ? 's' : ''} for{' '}
                  <span className="fw-bold" style={{color:'var(--primary)'}}>“{clue.trim()}”</span>
                  {pattern.replace(/_/g,'').length > 0 && <> matching <code style={{color:'var(--primary)'}}>{pattern}</code></>}
                </p>
                {results.count === 0 ? (
                  <p className="text-muted mb-0">
                    No definition match found. Try simpler wording, remove known letters, or switch to
                    pattern search below. Note: names and places aren't in the dictionary.
                  </p>
                ) : (
                  <>
                    <div className="d-flex flex-column gap-2">
                      {results.results?.map(r => (
                        <div key={r.word} className="d-flex align-items-baseline gap-2 pb-2"
                          style={{borderBottom:'1px solid #eef0fb'}}>
                          <DefinableWord word={r.word} className="fw-bold"
                            style={{fontSize:'1.05rem', letterSpacing:1, textTransform:'uppercase',
                                    color:'#1e293b', textDecoration:'none', flexShrink:0}}>
                            {r.word.toUpperCase()}
                          </DefinableWord>
                          <span className="text-muted" style={{fontSize:'0.85rem', lineHeight:1.4}}>
                            {r.definition}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-muted small fw-bold mb-0 mt-2">
                      💡 Ranked by how closely each meaning fits your clue. Click a word for its full definition.
                    </p>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="result-count">
                  Found <strong>{results.count}</strong> matching word{results.count !== 1 ? 's' : ''} for pattern{' '}
                  <code style={{fontSize:'1.1rem', color:'var(--primary)'}}>{pattern}</code>
                </p>
                {results.count === 0 ? (
                  <p className="text-muted">No words match this pattern. Try a different pattern!</p>
                ) : (
                  <>
                    <div className="words-cloud">
                      {results.results?.map(r => (
                        <DefinableWord key={r.word} word={r.word} className="result-badge"
                          style={{fontSize:'1rem', textDecoration:'none'}}>
                          {r.word.toUpperCase()}
                        </DefinableWord>
                      ))}
                    </div>
                    <p className="text-muted small fw-bold mb-0 mt-2">
                      💡 Click any word to see its meaning.
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        )}
      <SeoContent game="crossword" />
      </div>
    </div>
  );
}
